import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { ILogger } from '@wz-services';
import { isStringNullOrEmpty } from '@wz-utilities/string.utilities';
import { PolicyModels } from 'models/policy.models';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export abstract class StepperServiceBase {
  public maxStep = 0;
  public currentStep = 0;
  public isStepperDisabled!: boolean;
  public keepCurrentRisk = false;

  private _quotationId!: string;
  public get quotationId() {
    return this._quotationId;
  }

  private _packageId!: PolicyModels.PackageEnum;
  public get packageId() {
    return this._packageId;
  }

  stepChange$ = new BehaviorSubject<StepperStepDefinition | null>(null);
  // quotationIdSubject = new BehaviorSubject<string>(null);
  // quotationIdChange$ = this.quotationIdSubject.asObservable();

  constructor(
    private logger: ILogger,
    private route: ActivatedRoute,
  ) {}

  abstract steps: StepperStepDefinition[];
  abstract navigate(route: string[]): any;
  protected abstract internalClear(): any;

  updateStepper(step: number) {
    this.maxStep = step;
    this.currentStep = step;
    if (step > 0) {
      this.keepCurrentRisk = true;
    }

    this.stepChange$.next(this.steps[step]);
  }

  enableStepper() {
    this.isStepperDisabled = false;
  }

  disableStepper() {
    this.isStepperDisabled = true;
  }

  setQuotationId(publicId: string | null) {
    if (publicId === this.quotationId) {
      return;
    }

    this._quotationId = publicId!;
    // this.quotationIdSubject.next(publicId);
  }

  setPackageId(id: PolicyModels.PackageEnum) {
    if (id === this.packageId) {
      return;
    }

    this._packageId = id;
  }

  quotationIdFromParams(route: ActivatedRoute) {
    return route.params.pipe(
      map((params) => {
        let quotationId = null;
        if (params) {
          const qId: string = params['quotationId'];
          if (qId) {
            quotationId = qId;
          }
        }

        this.setQuotationId(quotationId!);
        return quotationId;
      }),
      filter((qId) => !!qId),
    );
  }

  packageIdFromParams(route: ActivatedRoute) {
    const packageId = (route.snapshot.parent as any).data.packageId;
    this.setPackageId(packageId);
  }

  getPackageIdFromRoute(route: ActivatedRouteSnapshot | any): any {
    if (route == null) return null;
    if (route.data.packageId != null) return route.data.packageId;
    else {
      return this.getPackageIdFromRoute(route.firstChild!);
    }
  }

  getPackageName(packageId: PolicyModels.PackageEnum) {
    switch (packageId) {
      case PolicyModels.PackageEnum.Sante_InfinitySante:
        return 'infinity';
      case PolicyModels.PackageEnum.Sante_Fidelity:
        return 'fidelity';
      case PolicyModels.PackageEnum.Sante_Acheel:
        return 'eco-plus';
      default:
        throw new Error('unknown health packageId');
    }
  }

  goToStep(step: number) {
    if (this.steps[step]) {
      const avoidNavigation = step === this.currentStep;
      if (avoidNavigation) {
        return;
      }

      const currentPackageId =
        this._packageId || this.getPackageIdFromRoute(this.route.snapshot);

      const packageName = this.getPackageName(currentPackageId);

      if (!isStringNullOrEmpty(this.quotationId)) {
        this.navigate([
          this.steps[step].route.format(packageName, this.quotationId),
        ]);
      } else {
        this.navigate([this.steps[step].route.format(packageName)]);
      }
      // this.updateStepper(step);
    } else {
      this.logger.error(`The step ${step} did not have any stepDefinition`);
    }
  }

  clear(): any {
    this.setQuotationId(null);
    this.currentStep = 0;
    this.maxStep = 0;
    this.isStepperDisabled = false;
    this.internalClear();
  }
}

export interface StepperStepDefinition {
  label: string;
  route: string;
  containerClass?: string;
}
